/**
 * The confirmation after a send (PLAN.md 4.31): Write announces the sealed capsule and the page it
 * leads to (Sent or Correspondence) shows it once. Kept in this tab's memory only; leaving that
 * page, or closing the notice, ends it.
 */
import { useSyncExternalStore } from "react";
import type { Event } from "@timecapsule/crypto";

export type Sealed = {
  /** The page path (without query) the notice belongs to. */
  on: string;
  /** Unix milliseconds of the opening. */
  unlockAt: number;
  audience: { kind: "public" } | { kind: "self" } | { kind: "people"; count: number };
  /** A capsule the author can open later (public, or the author's copy), for "Download a copy". */
  copy: Event | null;
  /** The capsule has files, which a single capsule file does not carry. */
  files: boolean;
};

let current: Sealed | null = null;
const listeners = new Set<() => void>();

export function announceSealed(sealed: Sealed | null): void {
  current = sealed;
  for (const listener of listeners) listener();
}

export function useSealed(): Sealed | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
  );
}
